/**
 * The single logout implementation for the portal.
 *
 * Before the Redux removal this lived in `logoutHelper` (legacy
 * `src/utils/helper.js:416`), which dispatched a `logout` thunk to revoke the
 * token server-side and reset the `user` and `affiliate` slices. The migration
 * dropped both dispatches and left the function as a husk that only unregisters
 * the tracker, so `/logout` and the nav menu stopped ending the session. This
 * restores that behaviour on top of Zustand.
 */
import PushNotification from '@/PushNotification';
import { resetAnalytics } from '@/modules/shared/analytics';
import { useAppStore } from '@/modules/shared/store/appStore';
import { clearCookies, clearLocalData, unregisterTracking } from '@/utils/helper';
// A5b (`cb8149055`) left one implementation of the axios header helpers, in useTokenManager.
// main still exports a second copy from utils/helper; this branch does not.
import { removeAxiosHeaders } from './hooks/useTokenManager';

import { logout as revokeToken } from './api';
import { broadcastLogout } from './authChannel';
import { useAuthStore } from './store/useAuthStore';

/**
 * Drop every trace of the session held by this tab.
 *
 * Exported separately from `performLogout` because a tab that *receives* a logout
 * broadcast must clean up without revoking again (the token is already gone) or
 * re-broadcasting (which would ping-pong between tabs).
 */
export function clearClientSession(): void {
  clearCookies();
  clearLocalData();
  removeAxiosHeaders();
  useAppStore.getState().setUser(null);
  useAppStore.getState().setAffiliate(null);
  useAuthStore.getState().clearAuthFlow();

  resetAnalytics();
  unregisterTracking();
  PushNotification?.unregister?.();
}

/**
 * End the session everywhere: server-side, in this tab, and in every other tab.
 *
 * Always lands on a full page load rather than a router navigation, so in-memory
 * Zustand stores and the TanStack Query cache are torn down with the document
 * instead of surviving into the logged-out view.
 */
export async function performLogout(redirectTo = '/'): Promise<void> {
  try {
    await revokeToken();
  } catch {
    // Offline or server error. Clear locally anyway; the token expires on its own.
  }

  clearClientSession();
  broadcastLogout();

  window.location.replace(redirectTo);
}
